import { useState } from 'react';
import { Button } from '@higgins/ui';
import { DEFAULT_SCALE_LABEL, SCALE_LABELS } from '../lib/condition';
import { startVisit } from '../lib/condition-records';
import type { ConditionContractSummary } from '../lib/types';

/** The rating scales as a row of choices: when a report starts, and when its scale changes (#473). */
export function ScalePicker({ value, onChange, disabled = false }: { value: string; onChange: (label: string) => void; disabled?: boolean }) {
  return (
    <div className="mt-1 flex flex-wrap gap-2">
      {SCALE_LABELS.map((label) => (
        <button
          key={label}
          type="button"
          onClick={() => onChange(label)}
          disabled={disabled}
          aria-pressed={value === label}
          className={`min-h-11 rounded-full border px-3 text-sm disabled:opacity-50 ${value === label ? 'border-primary bg-orange-50 text-orange-800' : 'border-slate-300 bg-white text-slate-700'}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * The Condition tab on a job with no condition report yet: start it, once.
 * The centre is made from the job when the visit reaches CRM.
 */
export function ConditionStart({ job, centre, hasMeasure }: { job: { id: string; number: string; conditionContract?: ConditionContractSummary | null }; centre: string; hasMeasure: boolean }) {
  // The client's Condition contract sets the scale a report starts on; the measurer can still change it.
  const contract = job.conditionContract ?? null;
  const [scale, setScale] = useState(contract?.scale && SCALE_LABELS.includes(contract.scale) ? contract.scale : DEFAULT_SCALE_LABEL);
  const [starting, setStarting] = useState(false);

  async function start() {
    setStarting(true);
    try {
      await startVisit(job, scale, hasMeasure);
    } finally {
      setStarting(false);
    }
  }

  return (
    <div className="max-w-xl space-y-4 rounded-lg border border-primary bg-white p-4">
      <div>
        <h3 className="text-base font-semibold text-slate-900">Start the condition report for {job.number}</h3>
        <p className="mt-1 text-sm text-slate-600">Once per job. Every area then gets its condition on this tab, offline as well.</p>
      </div>
      <div>
        <p className="text-xs text-slate-500">Centre</p>
        <p className="text-sm text-slate-900">{centre}</p>
        <p className="text-xs text-slate-500">Set up in CRM from the job when it syncs.</p>
      </div>
      <div>
        <p className="text-xs text-slate-500">Rating scale</p>
        <ScalePicker value={scale} onChange={setScale} />
        <p className="mt-1 text-xs text-slate-500">
          {contract?.scale ? `${contract.name} uses ${contract.scale}. ` : ''}You can change it later on this tab.
        </p>
      </div>
      <Button onClick={() => void start()} disabled={starting}>
        Start
      </Button>
    </div>
  );
}
